import type { TaxonomyNode } from '@/api/generatedApi';
import { useGetTaxonomyChildren } from '@/hooks/api/taxonomy/useGetTaxonomyChildren';
import { useNodeAncestorPath } from '@/hooks/api/taxonomy/useNodeAncestorPath';
import { useTreeSearch } from '@/hooks/api/taxonomy/useTreeSearch';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { Search } from '@mui/icons-material';
import { Box, CircularProgress, InputAdornment, Popover, TextField, Typography } from '@mui/material';
import { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import { PickerNodeLevel } from './PickerNodeLevel';

export const LabelPickerContent = ({
  taxonomyId,
  currentNodeId,
  onSelect,
  onRemove,
}: {
  taxonomyId: string;
  currentNodeId: string | undefined;
  onSelect: (node: TaxonomyNode, depth: number) => void;
  onRemove: (() => void) | undefined;
}) => {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const expandedSet = useMemo(() => new Set(expandedItems), [expandedItems]);
  const scrollContainerReference = useRef<HTMLElement>(null);

  const { nodes: rootNodes, isLoading } = useGetTaxonomyChildren(taxonomyId, 'root');
  const { ancestorPath } = useNodeAncestorPath(taxonomyId, currentNodeId);
  const { isSearching, isSearchPending, searchVisibleIds, searchMatchIds, searchExpandedIds } = useTreeSearch(
    taxonomyId,
    searchQuery
  );

  useEffect(() => {
    if (ancestorPath && ancestorPath.length > 1) {
      const ancestors = ancestorPath.slice(0, -1);
      setExpandedItems(previous => {
        const newIds = ancestors.filter(id => !previous.includes(id));
        return newIds.length > 0 ? [...previous, ...newIds] : previous;
      });
    }
  }, [ancestorPath]);

  useEffect(() => {
    if (searchExpandedIds.length === 0) return;
    setExpandedItems(previous => {
      const newIds = searchExpandedIds.filter(id => !previous.includes(id));
      return newIds.length > 0 ? [...previous, ...newIds] : previous;
    });
  }, [searchExpandedIds]);

  return (
    <Box sx={{ p: 2 }}>
      <TextField
        size='small'
        placeholder='Search...'
        value={searchQuery}
        onChange={event => setSearchQuery(event.target.value)}
        autoFocus
        fullWidth
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position='start'>
                {isSearching && isSearchPending ? (
                  <CircularProgress size={16} />
                ) : (
                  <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                )}
              </InputAdornment>
            ),
            sx: { fontSize: '0.875rem' },
          },
        }}
        sx={{ mb: 1.5 }}
      />
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {!isLoading && rootNodes.length === 0 && (
        <Typography variant='body2' color='text.secondary' sx={{ py: 2, textAlign: 'center' }}>
          No categories available.
        </Typography>
      )}
      {!isLoading && rootNodes.length > 0 && (
        <Box ref={scrollContainerReference} sx={{ maxHeight: 400, overflow: 'auto' }}>
          <SimpleTreeView
            expandedItems={expandedItems}
            onExpandedItemsChange={(_event, itemIds) => {
              startTransition(() => setExpandedItems(itemIds));
            }}
            sx={{
              '& .MuiTreeItem-content': { borderRadius: 1, py: 0.25 },
              '& .MuiTreeItem-iconContainer': { color: 'text.secondary' },
              '& .MuiTreeItem-groupTransition': {
                ml: 2.5,
                pl: 1.5,
                borderLeft: '2px solid',
                borderColor: 'divider',
              },
            }}
          >
            <PickerNodeLevel
              taxonomyId={taxonomyId}
              parentNodeId='root'
              depth={0}
              expandedSet={expandedSet}
              currentNodeId={currentNodeId}
              ancestorPath={ancestorPath}
              scrollContainerRef={scrollContainerReference}
              onSelect={onSelect}
              onRemove={onRemove}
              searchMatchIds={searchMatchIds}
              searchVisibleIds={searchVisibleIds}
            />
          </SimpleTreeView>
        </Box>
      )}
    </Box>
  );
};

export const LabelPicker = ({
  taxonomyId,
  currentNodeId,
  anchorEl,
  open,
  onClose,
  onSelect,
  onRemove,
}: {
  taxonomyId: string;
  currentNodeId: string | undefined;
  anchorEl: HTMLElement | undefined;
  open: boolean;
  onClose: () => void;
  onSelect: (node: TaxonomyNode, depth: number) => void;
  onRemove: (() => void) | undefined;
}) => {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: { borderRadius: '12px', mt: 0.5, width: 420 },
        },
      }}
    >
      <LabelPickerContent
        taxonomyId={taxonomyId}
        currentNodeId={currentNodeId}
        onSelect={onSelect}
        onRemove={onRemove}
      />
    </Popover>
  );
};
